import { h, Component, Prop, Element, Watch, Build, Method } from '@stencil/core';

import * as am5 from '@amcharts/amcharts5/index';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { addAxisLabelsToXYChart } from '../chartUtils';
const generateMockData = () => {
  const countries = ['USA', 'China', 'Japan', 'Germany', 'UK', 'France', 'India', 'Spain', 'Netherlands', 'South Korea', 'Canada'];
  return countries.map(c => ({ country: c, value: Math.random() * 2000 }));
};
@Component({
  tag: 'udp-bar-chart',
  styleUrl: 'udp-bar-chart.css',
  shadow: true,
})
export class UdpBarChart {
  @Element() el: HTMLElement;

  @Prop() data: { [key: string]: any }[] = Build.isDev ? generateMockData() : undefined;
  @Prop() valueField: string = Build.isDev ? 'value' : undefined;
  @Prop() labelField: string = Build.isDev ? 'country' : undefined;
  @Prop() title: string = Build.isDev ? 'Test Header' : undefined;
  @Prop() subheader: string = Build.isDev ? 'Test subheader' : undefined;
  @Prop() size: 'small' | 'medium' | 'large' = 'medium';
  @Prop() customWidth?: number; // Rather then setting a size you can set a custom width
  @Prop() customHeight?: number; // Rather then setting a size you can set a custom height
  @Prop() customCardWidth?: number; // Rather then setting a size you can set a custom card width
  @Prop() customCardStyle?: { [key: string]: string } = {};
  @Prop() kpisPosition: 'left' | 'top' = 'left';
  @Prop() kpis: { value: string; label: string }[] = Build.isDev
    ? [
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
      ]
    : undefined;
  @Prop() xLabel: string = Build.isDev ? 'Test X Label' : undefined;
  @Prop() yLabel: string = Build.isDev ? 'Test Y Label' : undefined;
  @Prop() hideAdornment: boolean = false;
  @Prop() valueFields: {fieldName: string, fieldLabel: string}[] = undefined;
  @Prop() stacked: boolean = false;
  @Prop() showBullets: boolean = false;
  @Prop() barWidth: number = undefined;

  private chartId = 'udp-bar-chart';
  private chartRoot: am5.Root;

  @Method()
  makeSeries(chart, xAxis, yAxis, serieName, fieldName, root, singleSerie) {
    var series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: serieName,
        stacked: this.stacked,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: fieldName,
        categoryXField: this.labelField,
      }),
    );

    series.columns.template.setAll({
      tooltipText: singleSerie ? '{valueY}' : '{name}: {valueY}',
      width: this.barWidth ? am5.percent(this.barWidth) : null,
      tooltipY: 0,
      strokeOpacity: 0,
    });

    if (singleSerie) {
      series.columns.template.setAll({ cornerRadiusTL: 5, cornerRadiusTR: 5, strokeOpacity: 0 });
      series.columns.template.adapters.add('fill', function (_, target) {
        return chart.get('colors').getIndex(series.columns.indexOf(target));
      });

      series.columns.template.adapters.add('stroke', function (_, target) {
        return chart.get('colors').getIndex(series.columns.indexOf(target));
      });
    }

    series.data.setAll(this.data);

    if (this.showBullets) {
      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            text: '{valueY}',
            fill: root?.interfaceColors?.get('alternativeText'),
            centerY: am5.p50,
            centerX: am5.p50,
            populateText: true,
          }),
        });
      });
    }

    series.appear();
  }

  @Watch('valueField')
  @Watch('labelField')
  @Watch('data')
  @Watch('xLabel')
  @Watch('yLabel')
  @Watch('valueFields')
  @Watch('stacked')
  @Watch('showBullets')
  async renderChart() {
    if (!this.valueField && (!this.valueFields || this.valueFields?.length == 0)) return;
    // sometimes the element cannot be found right away so loop umtil it loads
    if (this.el?.shadowRoot == null || this.el.shadowRoot.getElementById(this.chartId) == null) {
      await setTimeout(this.renderChart, 50);
    }
    if (this.chartRoot) {
      this.chartRoot.dispose();
    }
    this.chartRoot = am5.Root.new(this.el.shadowRoot.getElementById(this.chartId));

    this.chartRoot.setThemes([am5themes_Animated.new(this.chartRoot)]);

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    var chart = this.chartRoot.container.children.push(
      am5xy.XYChart.new(this.chartRoot, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        paddingLeft: 0,
        paddingRight: 1,
        layout: this.chartRoot.verticalLayout,
      }),
    );

    // Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    var cursor = chart.set('cursor', am5xy.XYCursor.new(this.chartRoot, {}));
    cursor.lineY.set('visible', false);

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    var xRenderer = am5xy.AxisRendererX.new(this.chartRoot, {
      minGridDistance: 30,
      minorGridEnabled: true,
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
    });

    if (!this.valueFields || this.valueFields?.length == 0) {
      xRenderer.labels.template.setAll({
        rotation: -90,
        centerY: am5.p50,
        centerX: am5.p100,
        paddingRight: 15,
      });
    }

    xRenderer.grid.template.setAll({
      location: 1,
    });

    var xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.chartRoot, {
        maxDeviation: 0.3,
        categoryField: this.labelField,
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(this.chartRoot, {}),
      }),
    );
    xAxis.data.setAll(this.data);

    var yRenderer = am5xy.AxisRendererY.new(this.chartRoot, {
      strokeOpacity: 0.1,
    });

    xAxis.get('renderer').labels.template.setAll({
      maxHeight: 75,
      oversizedBehavior: 'truncate', // or "wrap" if you want to wrap the text instead
      textAlign: 'center',
      ellipsis: '...',
    });

    var yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.chartRoot, {
        maxDeviation: 0.3,
        renderer: yRenderer,
      }),
    );

    addAxisLabelsToXYChart(this.chartRoot, chart, this.xLabel, this.yLabel);
    // Create series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
    if (this.valueFields?.length > 0) {
      var legend = chart.children.push(
        am5.Legend.new(this.chartRoot, {
          centerX: am5.percent(50),
          x: am5.percent(50),
          marginTop: 15,
          marginBottom: 15,
        }),
      );
      this.valueFields.forEach((field) => {
        this.makeSeries(chart, xAxis, yAxis, field.fieldLabel, field.fieldName, this.chartRoot, false);
      });
      legend.data.setAll(chart.series.values);
    } else {
      this.makeSeries(chart, xAxis, yAxis, 'Series 1', this.valueField, this.chartRoot, true);
    }

    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    chart.appear(1000, 100);
  }

  disconnectedCallback() {
    // Clean up chart instance on component removal
    if (this.chartRoot) {
      this.chartRoot.dispose();
    }
  }
  componentDidLoad() {
    this.renderChart();
  }
  render() {
    return (
      <chart-container
        adjustChartWidthForKpis
        chartId={this.chartId}
        title={this.title}
        subheader={this.subheader}
        size={this.size}
        customWidth={this.customWidth}
        customHeight={this.customHeight}
        customCardWidth={this.customCardWidth}
        customCardStyle={this.customCardStyle}
        kpisPosition={this.kpisPosition}
        kpis={this.kpis}
        hideAdornment={this.hideAdornment}
      />
    );
  }
}
